/** HealBid support assistant — rule-based, no external API. Order: most specific → general. */

function normalize(userText) {
  return String(userText || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ");
}

/** User is asking for a way to call / reach HealBid admin support by phone. */
export function wantsCarePhoneIntent(userText) {
  const t = normalize(userText);
  if (!t) return false;
  if (/\b(911|112|108|emergency services|ambulance)\b/.test(t)) return false;
  if (
    /\b(phone|number|call|contact|reach|telephone|mobile|whatsapp)\b/.test(t) &&
    /\b(admin|care team|support|healbid|office|customer service|help desk|representative)\b/.test(t)
  ) {
    return true;
  }
  if (/\b(what|give|tell|share)\b.*\b(phone|number|call)\b/.test(t) && /\b(you|your|healbid|care|support|admin)\b/.test(t))
    return true;
  if (/\b(can|may|how)\s+i\b.*\b(call|phone|reach)\b/.test(t)) return true;
  if (/\bphone number\b/.test(t) || /\bcall you\b/.test(t)) return true;
  return false;
}

/** @typedef {{ match: (t: string) => boolean, reply: string }} Intent */

/** @type {Intent[]} */
const INTENTS = [
  {
    match: (t) =>
      /\b(suicid|self[- ]harm|kill myself|end my life)\b/.test(t) ||
      /\b(severe chest pain|heart attack|stroke|can't breathe|cant breathe|unconscious|bleeding heavily)\b/.test(t),
    reply:
      "If you or someone else may be in danger or having a medical emergency, call your local emergency number right away. HealBid cannot provide emergency care. When you are safe, we can help with app, bids, or documents — or connect you with our care team.",
  },
  {
    match: (t) => /\b(emergency|urgent care|ambulance|911|112|108)\b/.test(t),
    reply:
      "For life-threatening emergencies, contact emergency services immediately. HealBid helps with non-emergency care navigation (requests, bids, documents). If it is not an emergency, tell me what step you are on.",
  },
  {
    match: (t) =>
      /\b(insurance|policy|claim|cashless|tpa|pre[- ]?auth|preauth|coverage|deductible)\b/.test(t),
    reply:
      "Add your insurance card and policy details with your documents so providers can plan cashless or reimbursement paths. After verification, bids may include coverage-related notes. If a file was rejected, re-upload a clearer scan. Need account help? Our care team can review specifics with you.",
  },
  {
    match: (t) =>
      /\b(refund|invoice|receipt|payment failed|paid|charge|billing|money back)\b/.test(t),
    reply:
      "Billing depends on the hospital or doctor you booked with. In the app, open your request or appointment details for payment context. For disputes or refunds, our care team can coordinate with finance — use “Chat with a care team member” when you are ready.",
  },
  {
    match: (t) =>
      /\b(login|log in|password|otp|sign in|can't sign|cant sign|error|bug|not working|crash|slow)\b/.test(t),
    reply:
      "Try updating the app, checking your network, and requesting a fresh OTP. Clear old tabs or use one browser profile. If uploads fail, use a smaller PDF or a well-lit photo. Still stuck? Tell me the exact screen or error text, or connect with the care team for hands-on help.",
  },
  {
    match: (t) =>
      /\b(how long|when will|still pending|taking forever|delayed|status stuck|no update)\b/.test(t),
    reply:
      "Document verification is often within one business day; complex cases can take longer. Bids appear after verification when providers respond. You will see status changes on your request and notifications. If a deadline passed with no movement, our team can nudge the right party.",
  },
  {
    match: (t) =>
      /\b(cancel|reschedule|change appointment|move slot|can't come|cant come)\b/.test(t) &&
      /\b(appointment|visit|slot|booking)\b/.test(t),
    reply:
      "Open your request → appointment or booking flow to see available actions. Policies depend on the provider and how close the visit is. If the app does not offer the change you need, the care team can message the hospital or doctor on your behalf.",
  },
  {
    match: (t) =>
      /\b(messages|chat with doctor|provider chat|doctor message)\b/.test(t) &&
      /\b(not|where|how|different|support)\b/.test(t),
    reply:
      "Doctor and hospital threads in Messages are for bids, booking, and appointment updates (not free-text). For general questions or account help, stay in this support chat with the assistant, then hand off to the care team if needed.",
  },
  {
    match: (t) => /\b(notification|alert|bell|email update)\b/.test(t),
    reply:
      "Enable notifications in your device settings and keep the app updated. Time-sensitive steps (like holding a slot) usually push an alert. You can also review alerts inside the app’s notifications area.",
  },
  {
    match: (t) =>
      /\b(profile|phone number|change mobile|edit name|avatar|language)\b/.test(t),
    reply:
      "Open Profile in the app to update your details. Mobile or name changes that require OTP will walk you through verification. If something is blocked (for example pending verification), the care team can help.",
  },
  {
    match: (t) => /\b(privacy|data|delete account|gdpr|export)\b/.test(t),
    reply:
      "Your data is used to run matching, bids, and care coordination per our policies. For data export or account deletion requests, use in-app legal or settings where available, or ask the care team to route you to the right workflow.",
  },
  {
    match: (t) =>
      /\b(counter|negotiate|lower price|better offer|compare|which doctor|choose)\b/.test(t) &&
      /\b(bid|price|offer|doctor|hospital)\b/.test(t),
    reply:
      "You can compare bid amounts, timelines, and notes side by side. If a provider countered, accept or decline within the time shown. Choosing a provider locks in that path for booking — pick the option that best matches cost, location, and recovery time you need.",
  },
  {
    match: (t) =>
      /\b(bid|bidding|quote|price|cost|estimate|how much)\b/.test(t) ||
      /\b(accept bid|reject bid|choose bid)\b/.test(t),
    reply:
      "After your documents are verified, doctors or hospitals submit bids on your request. Compare amount, timeline, and notes, then accept the bid that fits you. You can return to your request anytime to review or act before offers expire.",
  },
  {
    match: (t) =>
      /\b(verify|verification|document|upload|prescription|mri|report|lab|scan)\b/.test(t) ||
      /\b(reject|rejected|resubmit|unclear|blurry)\b/.test(t),
    reply:
      "Upload clear photos or PDFs: prescription, reports, and insurance where applicable. Avoid glare and cropped edges. Status moves from pending review to verified when the team is satisfied. If rejected, read the reason and re-upload a corrected file.",
  },
  {
    match: (t) =>
      /\b(appointment|book|schedule|slot|visit time|hospital visit)\b/.test(t) ||
      /\b(hold slot|slot held|confirm visit)\b/.test(t),
    reply:
      "After you accept a bid, pick an appointment slot with that provider. Confirmed visits show on your dashboard. Some steps are time-sensitive — complete actions when prompted so you do not lose a held slot.",
  },
  {
    match: (t) =>
      /\b(location|address|pincode|pin code|near me|distance|map)\b/.test(t) &&
      /\b(hospital|doctor|visit|clinic)\b/.test(t),
    reply:
      "Provider locations and hospital names appear on bids and booking screens. Use the details there for directions. If an address looks wrong, tell the care team which request and provider so they can verify.",
  },
  {
    match: (t) =>
      /\b(what is healbid|how does healbid|how healbid works|explain healbid|why healbid)\b/.test(t),
    reply:
      "HealBid helps you request care, get verified, receive transparent bids from providers, compare options, and book a visit — with status updates along the way. Ask me about documents, bids, appointments, or insurance basics anytime.",
  },
  {
    match: (t) =>
      /^(hi|hello|hey|good morning|good afternoon|good evening)\b|^hii?$/.test(t) ||
      /\b(help me|i need help|can you help)\b/.test(t),
    reply:
      "Hi — I am the HealBid assistant. I can walk you through documents & verification, bids & pricing, appointments, insurance basics, or app issues. What is one thing you want to solve right now?",
  },
  {
    match: (t) => /\b(thank|thanks|thx|appreciate)\b/.test(t),
    reply:
      "Glad to help. If something else comes up, ask here — or tap “Chat with a care team member” for a human on live chat.",
  },
  {
    match: (t) => /\b(bye|goodbye|see you|that's all|thats all)\b/.test(t),
    reply: "Take care. Come back anytime if you need more help with your care journey.",
  },
  {
    match: (t) => /\b(human|agent|person|real|someone|care team|support staff|operator|live person)\b/.test(t),
    reply:
      "I can bring a care team member into this chat. Tap “Chat with a care team member” below whenever you are ready.",
  },
];

function guessTopicHints(t) {
  const hints = [];
  if (/\b(upload|file|pdf|jpg|verify|reject|document)\b/.test(t)) hints.push("documents & verification");
  if (/\b(bid|price|offer|counter|doctor|hospital)\b/.test(t)) hints.push("bids & providers");
  if (/\b(appointment|slot|book|visit|schedule)\b/.test(t)) hints.push("booking & visits");
  if (/\b(insurance|policy|claim)\b/.test(t)) hints.push("insurance");
  if (/\b(login|otp|error|bug)\b/.test(t)) hints.push("app & sign-in");
  return hints;
}

/**
 * @param {string} userText
 * @param {{ patientCarePhone?: string }} [opts]
 */
export function botReply(userText, opts = {}) {
  const t = normalize(userText);
  if (!t) {
    return "Type your question in a short sentence — for example: “My upload was rejected” or “How do I accept a bid?”";
  }

  const phone = String(opts.patientCarePhone || "").trim();
  if (wantsCarePhoneIntent(userText)) {
    if (phone) {
      return `Our care team phone number is ${phone}. You can call from your phone’s keypad, or keep chatting here — tap “Chat with a care team member” when you want a live conversation in the app.`;
    }
    return "A direct support phone number is not set up in the app yet. Tap “Chat with a care team member” below and the team can help you or share the best number to call.";
  }

  for (let i = 0; i < INTENTS.length; i += 1) {
    if (INTENTS[i].match(t)) return INTENTS[i].reply;
  }

  const hints = guessTopicHints(t);
  if (hints.length) {
    return `I am not fully sure yet, but it may relate to ${hints.join(" and ")}. Try rephrasing with one clear goal (what happened + what you want next). You can also use “Chat with a care team member” for a real person.`;
  }

  return "I do not have a specific answer for that yet. Try asking about documents & verification, bids & pricing, appointments, insurance, or app issues — or use “Chat with a care team member” below for live help.";
}

export function wantsEscalation(userText) {
  const t = normalize(userText);
  if (
    /\b(human|agent|person|real (person|human)|talk to someone|speak to someone|live (chat|agent)|care team member|representative|customer service)\b/.test(
      t,
    )
  ) {
    return true;
  }
  if (/\bconnect me to\b/.test(t) && /\b(team|person|agent|support)\b/.test(t)) return true;
  if (/\b(speak to|talk to) (a |the )?(manager|supervisor)\b/.test(t)) return true;
  if (/\b(this is )?not helping\b/.test(t) && /\b(escalat|human|person)\b/.test(t)) return true;
  if (/\bescalate\b/.test(t) && /\b(this|issue|problem|now)\b/.test(t)) return true;
  return false;
}
